import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { MODELS, MODEL_STORAGE_KEY, getSavedModel } from '../lib/models'
import './SettingsDrawer.css'

export function SettingsDrawer() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [model, setModel] = useState(getSavedModel)
  const drawerRef = useRef(null)

  // ESC key to close
  useEffect(() => {
    if (!open) return
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      // Trigger slide-in animation
      requestAnimationFrame(() => {
        drawerRef.current?.classList.add('open')
      })
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  const handleModelChange = (e) => {
    const val = e.target.value
    setModel(val)
    try { localStorage.setItem(MODEL_STORAGE_KEY, val) } catch {}
  }

  const handleSignOut = async () => {
    setOpen(false)
    await signOut()
    navigate('/login')
  }

  const initial = (user?.email || '?')[0].toUpperCase()

  return (
    <>
      {!open && (
        <button
          className="settings-fab"
          onClick={() => setOpen(true)}
          title="Settings"
          aria-label="Open settings"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      )}

      {open && (
        <>
          <div className="settings-backdrop" onClick={() => setOpen(false)} />
          <div className="settings-drawer" ref={drawerRef}>
            <div className="settings-drawer-header">
              <h2>Settings</h2>
              <button className="settings-close-btn" onClick={() => setOpen(false)} aria-label="Close settings">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="settings-drawer-body">
              {/* Account */}
              <div className="settings-section">
                <div className="settings-section-title">Account</div>
                <div className="settings-profile-card">
                  <div className="settings-profile-info">
                    <div className="settings-avatar bg-purple-850 text-white">
                      {initial}
                    </div>
                    <div>
                      <div className="settings-profile-email">{user?.email}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Default Model */}
              <div className="settings-section">
                <div className="settings-section-title">Default Model</div>
                <select
                  className="settings-model-select"
                  value={model}
                  onChange={handleModelChange}
                >
                  {MODELS.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.label} — {m.note}
                    </option>
                  ))}
                </select>
                <p className="settings-model-hint">
                  Used as the default across all tools. Can be changed per-tool.
                </p>
              </div>
            </div>

            <div className="settings-drawer-footer">
              <button className="settings-signout-btn" onClick={handleSignOut}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
